import { ThunkAction, ThunkDispatch } from 'redux-thunk';

import { DateTools, Utils } from '@treezor/design-system';

import {
  IOperationProofOfPayoutDownload,
  IWallet,
  IWalletAccountDetailsDownload,
  IWalletBalanceCertificateDownload,
  IWalletCreation,
  IWalletDomiciliationCertificateDownload,
  IWalletStatementDownload,
} from '@/features/wallets/store/models/wallet.model';
import { WalletService } from '@/features/wallets/services/WalletService';
import { WalletActionTypes } from '@/features/wallets/store/types/wallet.types';
import {
  IOperationSimulation,
  IWalletOperationDownload,
  TWalletOperation,
} from '@/features/wallets/store/models/walletOperation.model';

import { IProfileState, TProfileAction } from '@/store/types/profile.types';
import { TAppError } from '@/store/types/base.state';
import { TCoreAction } from '@/store/types/modals.types';

import { TAppState } from '@/store/reducers/reducers';
import { closeModal } from '@/store/actions/core.actions';

import { ICursorElements } from '@/store/types/search.types';

import { DashboardHelpers } from '@/utils/DashboardHelpers';
import { WalletTools } from '@/utils/tools/WalletTools';

import { selectWalletOperationReportByUserIdAndWalletId } from '@/store/selectors';

export const createWallet = (
  wallet: Partial<IWalletCreation>,
  userId: number,
  onFinish?: (walletId: number) => void,
): ThunkAction<Promise<void>, IProfileState, {}, TProfileAction> => {
  const request = (): TProfileAction => ({
    type: WalletActionTypes.CREATE_WALLET,
    userId,
  });
  const fail = (error: TAppError): TProfileAction => ({
    type: WalletActionTypes.CREATE_WALLET_FAIL,
    userId,
    error,
    documentation: 'https://docs.treezor.com/guide/wallets/creation.html',
  });
  const success = (wallet: IWallet): TProfileAction => ({
    type: WalletActionTypes.CREATE_WALLET_SUCCESS,
    wallet,
  });

  return async (dispatch: ThunkDispatch<IProfileState, {}, TProfileAction>): Promise<void> => {
    dispatch(request());
    try {
      const newWallet = await WalletService.createWallet(wallet);
      dispatch(success(newWallet));
      onFinish?.(newWallet.walletId);
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const getWallet = (
  walletId: number,
  userId: number,
): ThunkAction<Promise<void>, TAppState, {}, TProfileAction> => {
  const request = (): TProfileAction => ({
    type: WalletActionTypes.GET_WALLET,
    walletId,
  });
  const fail = (error: TAppError): TProfileAction => ({
    type: WalletActionTypes.GET_WALLET_FAIL,
    walletId,
    error,
    documentation: 'https://docs.treezor.com/guide/wallets/introduction.html#key-attributes',
  });
  const success = (wallet: IWallet, userId: number): TProfileAction => ({
    type: WalletActionTypes.GET_WALLET_SUCCESS,
    shouldSkipToaster: true,
    userId,
    walletId,
    wallet,
  });

  return async (dispatch: ThunkDispatch<TAppState, {}, TProfileAction>): Promise<void> => {
    dispatch(request());
    try {
      const wallet = await WalletService.searchWallet(walletId);

      dispatch(success(wallet, userId));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const getWallets = (
  userId: number,
  shouldSearchBalance: boolean = true,
  pageNumber?: number,
  pageCount?: number,
): ThunkAction<Promise<void>, IProfileState, {}, TProfileAction> => {
  const request = (): TProfileAction => ({
    type: WalletActionTypes.GET_WALLETS,
    userId,
  });
  const fail = (error: TAppError): TProfileAction => ({
    type: WalletActionTypes.GET_WALLETS_FAIL,
    userId,
    shouldSkipToaster: true,
    error,
  });
  const success = (wallets: IWallet[]): TProfileAction => ({
    type: WalletActionTypes.GET_WALLETS_SUCCESS,
    shouldSkipToaster: true,
    userId,
    wallets,
  });

  return async (dispatch: ThunkDispatch<IProfileState, {}, TProfileAction>): Promise<void> => {
    dispatch(unsetProfileWallets(userId));
    dispatch(request());
    try {
      const wallets = await WalletService.searchWallets({ userId, pageNumber, pageCount }, shouldSearchBalance);

      dispatch(success(wallets));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const updateWallet = (
  userId: number,
  walletToUpdate: Partial<IWalletCreation>,
  walletId: number,
): ThunkAction<Promise<void>, IProfileState, {}, TProfileAction | TCoreAction> => {
  const request = (): TProfileAction => ({
    type: WalletActionTypes.UPDATE_WALLET,
    userId,
    walletId,
  });
  const fail = (error: TAppError): TProfileAction => ({
    type: WalletActionTypes.UPDATE_WALLET_FAIL,
    userId,
    walletId,
    error,
    documentation: 'https://docs.treezor.com/guide/wallets/introduction.html#key-attributes',
  });
  const success = (wallet: IWallet): TProfileAction => ({
    type: WalletActionTypes.UPDATE_WALLET_SUCCESS,
    wallet,
  });

  return async (dispatch: ThunkDispatch<IProfileState, {}, TProfileAction | TCoreAction>): Promise<void> => {
    dispatch(request());
    try {
      const wallet = await WalletService.updateWallet(walletToUpdate, walletId);
      dispatch(success(wallet));
      dispatch(closeModal());
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const getCurrentWalletOperations = (
  walletId: number,
  userId: number,
  operationsFilters?: Record<string, string>,
  cursor?: string | null,
): ThunkAction<Promise<void>, TAppState, {}, TProfileAction> => {
  const request = (): TProfileAction => ({
    type: WalletActionTypes.GET_CURRENT_WALLET_OPERATIONS,
    userId,
    walletId,
  });
  const fail = (error: TAppError): TProfileAction => ({
    type: WalletActionTypes.GET_CURRENT_WALLET_OPERATIONS_FAIL,
    userId,
    walletId,
    error,
    documentation: 'https://docs.treezor.com/guide/operations/introduction.html',
  });
  const success = (paginateOperations: ICursorElements<TWalletOperation>, userId: number): TProfileAction => ({
    type: WalletActionTypes.GET_CURRENT_WALLET_OPERATIONS_SUCCESS,
    shouldSkipToaster: true,
    paginateOperations,
    walletId,
    userId,
  });

  return async (dispatch: ThunkDispatch<TAppState, {}, TProfileAction>): Promise<void> => {
    dispatch(request());

    try {
      const paginateOperations = await WalletService.getOperations(walletId, userId, operationsFilters, cursor);
      dispatch(success(paginateOperations, userId));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const getWalletAccountDetail = (
  walletId: number,
  userId: number,
): ThunkAction<Promise<void>, IProfileState, {}, TProfileAction> => {
  const request = (): TProfileAction => ({
    type: WalletActionTypes.GET_WALLET_ACCOUNT_DETAILS,
    shouldSkipToaster: true,
    walletId,
    userId,
  });
  const fail = (error: TAppError): TProfileAction => ({
    type: WalletActionTypes.GET_WALLET_ACCOUNT_DETAILS_FAIL,
    error,
    userId,
    walletId,
    documentation: 'https://docs.treezor.com/guide/wallets/account-details.html',
  });
  const success = (walletAccountDetail: IWalletAccountDetailsDownload): TProfileAction => ({
    type: WalletActionTypes.GET_WALLET_ACCOUNT_DETAILS_SUCCESS,
    shouldSkipToaster: true,
    walletId,
    userId,
    walletAccountDetail,
  });

  return async (dispatch: ThunkDispatch<IProfileState, {}, TProfileAction>): Promise<void> => {
    dispatch(request());
    try {
      const walletAccountDetail = await WalletService.getWalletAccountDetail(walletId);
      dispatch(success(walletAccountDetail));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const getDomiciliationCertificate = (
  walletId: number,
  userId: number,
): ThunkAction<Promise<void>, IProfileState, {}, TProfileAction> => {
  const request = (): TProfileAction => ({
    type: WalletActionTypes.GET_WALLET_DOMICILIATION_CERTIFICATE,
    shouldSkipToaster: true,
    walletId,
    userId,
  });
  const fail = (error: TAppError): TProfileAction => ({
    type: WalletActionTypes.GET_WALLET_DOMICILIATION_CERTIFICATE_FAIL,
    error,
    userId,
    walletId,
  });
  const success = (domiciliationCertificate: IWalletDomiciliationCertificateDownload): TProfileAction => ({
    type: WalletActionTypes.GET_WALLET_DOMICILIATION_CERTIFICATE_SUCCESS,
    shouldSkipToaster: true,
    walletId,
    userId,
    domiciliationCertificate,
  });

  return async (dispatch: ThunkDispatch<IProfileState, {}, TProfileAction>): Promise<void> => {
    dispatch(request());
    try {
      const domiciliationCertificate = await WalletService.getDomiciliationCertficate(walletId);
      dispatch(success(domiciliationCertificate));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const getBalanceCertificate = (
  walletId: number,
  userId: number,
): ThunkAction<Promise<void>, IProfileState, {}, TProfileAction> => {
  const request = (): TProfileAction => ({
    type: WalletActionTypes.GET_WALLET_BALANCE_CERTIFICATE,
    shouldSkipToaster: true,
    walletId,
    userId,
  });
  const fail = (error: TAppError): TProfileAction => ({
    type: WalletActionTypes.GET_WALLET_BALANCE_CERTIFICATE_FAIL,
    error,
    userId,
    walletId,
  });
  const success = (balanceCertificate: IWalletBalanceCertificateDownload): TProfileAction => ({
    type: WalletActionTypes.GET_WALLET_BALANCE_CERTIFICATE_SUCCESS,
    shouldSkipToaster: true,
    walletId,
    userId,
    balanceCertificate,
  });

  return async (dispatch: ThunkDispatch<IProfileState, {}, TProfileAction>): Promise<void> => {
    dispatch(request());
    try {
      const balanceCertificate = await WalletService.getBalanceCertificate(walletId);
      dispatch(success(balanceCertificate));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const getClosureCertificate = (
  walletId: number,
  userId: number,
): ThunkAction<Promise<void>, IProfileState, {}, TProfileAction> => {
  const request = (): TProfileAction => ({
    type: WalletActionTypes.GET_WALLET_CLOSURE_CERTIFICATE,
    shouldSkipToaster: true,
    walletId,
    userId,
  });
  const fail = (error: TAppError): TProfileAction => ({
    type: WalletActionTypes.GET_WALLET_CLOSURE_CERTIFICATE_FAIL,
    error,
    userId,
    walletId,
  });
  const success = (closureCertificate: IWalletDomiciliationCertificateDownload): TProfileAction => ({
    type: WalletActionTypes.GET_WALLET_CLOSURE_CERTIFICATE_SUCCESS,
    shouldSkipToaster: true,
    walletId,
    userId,
    closureCertificate,
  });

  return async (dispatch: ThunkDispatch<IProfileState, {}, TProfileAction>): Promise<void> => {
    dispatch(request());
    try {
      const closureCertificate = await WalletService.getClosureCertificate(walletId);
      dispatch(success(closureCertificate));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const simulateSCTR = (
  wallet: IWallet,
  amount: number,
): ThunkAction<Promise<void>, IProfileState, {}, TProfileAction | TCoreAction> => {
  const request = (): TProfileAction => ({
    type: WalletActionTypes.SIMULATE_SCTR,
    userId: wallet.userId,
    walletId: wallet.walletId,
  });
  const fail = (error: TAppError): TProfileAction => ({
    type: WalletActionTypes.SIMULATE_SCTR_FAIL,
    userId: wallet.userId,
    walletId: wallet.walletId,

    error,
  });
  const success = (): TProfileAction => ({
    type: WalletActionTypes.SIMULATE_SCTR_SUCCESS,
    userId: wallet.userId,
    walletId: wallet.walletId,
  });

  return async (
    dispatch: ThunkDispatch<IProfileState | TAppState, {}, TProfileAction | TCoreAction>,
  ): Promise<void> => {
    dispatch(request());
    try {
      await WalletService.simulateSctr(wallet.iban, amount);
      dispatch(success());
      dispatch(closeModal());
      dispatch(getWallet(wallet.walletId, wallet.userId));
      dispatch(getCurrentWalletOperations(wallet.walletId, wallet.userId));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const simulateOperation = (
  userId: number,
  data: IOperationSimulation,
): ThunkAction<Promise<void>, IProfileState, {}, TProfileAction | TCoreAction> => {
  const request = (): TProfileAction => ({
    type: WalletActionTypes.SIMULATE_OPERATION,
    userId,
    walletId: data.walletId,
  });
  const fail = (error: TAppError): TProfileAction => ({
    type: WalletActionTypes.SIMULATE_OPERATION_FAIL,
    userId,
    error,
    walletId: data.walletId,
  });
  const success = (): TProfileAction => ({
    type: WalletActionTypes.SIMULATE_OPERATION_SUCCESS,
    userId,
    walletId: data.walletId,
  });

  return async (
    dispatch: ThunkDispatch<IProfileState | TAppState, {}, TProfileAction | TCoreAction>,
  ): Promise<void> => {
    dispatch(request());
    try {
      await WalletService.simulateOperation(data);
      dispatch(success());
      dispatch(closeModal());
      dispatch(getCurrentWalletOperations(data.walletId, userId));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const downloadStatement = (
  walletId: number,
  userId: number,
  month: string,
  year: string,
): ThunkAction<Promise<void>, IProfileState, {}, TProfileAction> => {
  const request = (): TProfileAction => ({
    type: WalletActionTypes.GET_WALLET_STATEMENT,
    shouldSkipToaster: true,
    walletId,
    userId,
  });
  const fail = (error: TAppError): TProfileAction => ({
    type: WalletActionTypes.GET_WALLET_STATEMENT_FAIL,
    error,
    userId,
    walletId,
    documentation: 'https://docs.treezor.com/guide/accounts-statements/introduction.html',
  });
  const success = (statement: IWalletStatementDownload): TProfileAction => ({
    type: WalletActionTypes.GET_WALLET_STATEMENT_SUCCESS,
    shouldSkipToaster: true,
    statement,
    walletId,
    userId,
  });

  return async (dispatch: ThunkDispatch<IProfileState, {}, TProfileAction>): Promise<void> => {
    dispatch(request());
    try {
      const walletStatement = await WalletService.getStatement(walletId, month, year);
      Utils.downloadFile(walletStatement.link, `statement-wallet${walletId}-${month}-${year}`);
      dispatch(success(walletStatement));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const setCurrentWallet = (userId: number, currentWalletId: number): TProfileAction => ({
  type: WalletActionTypes.SET_CURRENT_WALLET,
  userId,
  currentWalletId,
});

export const unsetProfileWallets = (userId: number): TProfileAction => ({
  type: WalletActionTypes.UNSET_WALLETS,
  userId,
});

export const deactivateWallet = (wallet: IWallet): ThunkAction<Promise<void>, IProfileState, {}, TProfileAction> => {
  const request = (): TProfileAction => ({
    type: WalletActionTypes.DEACTIVATE_WALLET,
    userId: wallet.userId,
    walletId: wallet.walletId,
  });
  const fail = (error: TAppError): TProfileAction => ({
    type: WalletActionTypes.DEACTIVATE_WALLET_FAIL,
    userId: wallet.userId,
    walletId: wallet.walletId,
    error,
    documentation: 'https://docs.treezor.com/guide/wallets/introduction.html#deletion',
  });
  const success = (wallet: IWallet): TProfileAction => ({
    type: WalletActionTypes.DEACTIVATE_WALLET_SUCCESS,
    wallet,
  });

  return async (dispatch: ThunkDispatch<IProfileState, {}, TProfileAction>): Promise<void> => {
    dispatch(request());
    try {
      const walletDeactivated = await WalletService.deactivateWallet(wallet.walletId);
      dispatch(success(walletDeactivated));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const requestOperationsReport = (
  userId: number,
  walletId: number,
  dates: { dateFrom: Date; dateTo: Date },
  isExpiredLink?: boolean,
): ThunkAction<Promise<void>, TAppState, {}, TProfileAction> => {
  const request = (): TProfileAction => ({
    type: WalletActionTypes.REQUEST_OPERATIONS_REPORT,
    shouldSkipToaster: true,
    userId,
    walletId,
  });
  const fail = (error: TAppError): TProfileAction => ({
    type: WalletActionTypes.REQUEST_OPERATIONS_REPORT_FAIL,
    shouldSkipToaster: true,
    userId,
    walletId,
    error,
  });
  const success = (operation: IWalletOperationDownload): TProfileAction => ({
    type: WalletActionTypes.REQUEST_OPERATIONS_REPORT_SUCCESS,
    shouldSkipToaster: true,
    userId,
    walletId,
    operation,
  });

  const storeDates = {
    dateFrom: dates.dateFrom ? DateTools.format(dates.dateFrom, 'iso') : '',
    dateTo: dates.dateTo ? DateTools.format(dates.dateTo, 'iso') : '',
  };

  const apiDates = {
    dateFrom: dates.dateFrom ? DateTools.format(DashboardHelpers.getUtcDateTime(dates.dateFrom), 'iso') : '',
    dateTo: dates.dateTo ? DateTools.format(DashboardHelpers.getUtcDateTime(dates.dateTo), 'iso') : '',
  };

  return async (dispatch: ThunkDispatch<TAppState, {}, TProfileAction>): Promise<void> => {
    dispatch(request());
    try {
      await WalletService.requestOperationsReport(walletId, apiDates);
      dispatch(
        success({
          ...storeDates,
          createdAt: DateTools.now().getTime(),
          status: 'PENDING',
        }),
      );
      dispatch(getOperationsReportAction(userId, walletId, apiDates, isExpiredLink));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const getOperationsReportAction = (
  userId: number,
  walletId: number,
  dates: { dateFrom: string; dateTo: string },
  isExpiredLink?: boolean,
): ThunkAction<Promise<void>, TAppState, {}, TProfileAction> => {
  const request = (): TProfileAction => ({
    type: WalletActionTypes.GET_OPERATIONS_REPORT,
    shouldSkipToaster: true,
    userId,
    walletId,
  });
  const fail = (error: TAppError): TProfileAction => ({
    type: WalletActionTypes.GET_OPERATIONS_REPORT_FAIL,
    userId,
    walletId,
    error,
  });
  const success = (operation: IWalletOperationDownload): TProfileAction => ({
    type: WalletActionTypes.GET_OPERATIONS_REPORT_SUCCESS,
    shouldSkipToaster: true,
    userId,
    walletId,
    operation,
  });

  return async (dispatch: ThunkDispatch<TAppState, {}, TProfileAction>, getState: () => TAppState): Promise<void> => {
    const params = selectWalletOperationReportByUserIdAndWalletId(getState(), userId)?.state?.[walletId];
    dispatch(request());
    try {
      const file = await WalletService.getOperationsReport(walletId, dates);

      const operationsParams = WalletTools.buildOperationsParams(dates, file, params);

      dispatch(success(operationsParams));

      if (isExpiredLink) dispatch(downloadOperationsReport(operationsParams, walletId, userId));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const downloadOperationsReport = (
  operationsParams: IWalletOperationDownload,
  walletId: number,
  userId: number,
): ThunkAction<void, TAppState, {}, TProfileAction> => {
  return (dispatch: ThunkDispatch<TAppState, {}, TProfileAction>): void => {
    if (operationsParams.url) {
      Utils.downloadFile(
        operationsParams.url,
        `report-operations-${walletId}-${operationsParams.dateFrom}-${operationsParams.dateTo}`,
      );
      dispatch(resetOperationReport(userId, walletId));
    }
  };
};

export const resetOperationReport = (userId: number, walletId: number): TProfileAction => ({
  type: WalletActionTypes.RESET_OPERATIONS_REPORT,
  userId,
  walletId,
});

export const getProofOfPayout = (
  objectId: string,
  walletId: number,
  userId: number,
): ThunkAction<Promise<void>, IProfileState, {}, TProfileAction> => {
  const request = (): TProfileAction => ({
    type: WalletActionTypes.GET_OPERATION_PROOF_OF_PAYOUT,
    shouldSkipToaster: true,
    walletId,
    userId,
    objectId,
  });
  const fail = (error: TAppError): TProfileAction => ({
    type: WalletActionTypes.GET_OPERATION_PROOF_OF_PAYOUT_FAIL,
    error,
    userId,
    walletId,
    objectId,
  });
  const success = (proofOfPayout: IOperationProofOfPayoutDownload): TProfileAction => ({
    type: WalletActionTypes.GET_OPERATION_PROOF_OF_PAYOUT_SUCCESS,
    shouldSkipToaster: true,
    walletId,
    userId,
    objectId,
    proofOfPayout,
  });

  return async (dispatch: ThunkDispatch<IProfileState, {}, TProfileAction>): Promise<void> => {
    dispatch(request());
    try {
      const proofOfPayout = await WalletService.getOperationProofOfPayout(objectId);
      dispatch(success(proofOfPayout));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};
